/**
 * Google Forms integration for the Contact page.
 *
 * This lets the React form push submissions straight into a Google Form
 * (and its linked Google Sheet) with NO backend.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HOW TO SET THIS UP (one-time, ~5 minutes):
 *
 * 1. Create a Google Form (forms.google.com) with one question per field
 *    below. Use "Short answer" for most, "Paragraph" for Message.
 *    Suggested question titles:
 *      Full Name, Company Name, Job Title, Email Address, Phone Number,
 *      Country / Location, Fleet Size, Vehicle Type, Message, How did you hear about us
 *
 * 2. Get the ACTION URL:
 *    - Click "Send" → link icon → copy the form URL, OR open the live form.
 *    - The action URL is the form's edit/view URL with the ID, ending in
 *      /formResponse. It looks like:
 *      https://docs.google.com/forms/d/e/1FAIpQLSxxxxxxxx/formResponse
 *
 * 3. Get each field's entry ID:
 *    - Open the LIVE form → right-click → "View page source" (or use the
 *      three-dots → "Get pre-filled link", fill dummy values, click "Get link",
 *      and read the entry.NUMBER values from the generated URL).
 *    - Each field has an id like entry.123456789.
 *
 * 4. Paste the action URL and entry IDs below, replacing every REPLACE_ME.
 *
 * 5. Restart the dev server. Done — submissions now flow into your Sheet.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const GOOGLE_FORM = {
  // Step 2 — paste your form's /formResponse URL here:
  actionUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSdAxT5nTb8ThPhKw4EO8iobN0xUtzwu5ac24ajYBVxopg3hSw/formResponse',

  // Step 3 — map each form field to its Google Form entry ID:
  fields: {
    name:      'entry.1365739615', // Full Name
    company:   'entry.1252919152', // Company Name
    jobTitle:  'entry.359411699',  // Job Title
    email:     'entry.409594091',  // Email Address
    phone:     'entry.1965762333', // Phone Number
    country:   'entry.1400158808', // Country / Location
    fleetSize: 'entry.1694417299', // Fleet Size
    fleetType: 'entry.1521565766', // Vehicle Type
    message:   'entry.1860719127', // Message
    source:    'entry.557288609',  // How did you hear about us
  },
};

// True only once you've replaced the placeholders.
export const isGoogleFormConfigured =
  !GOOGLE_FORM.actionUrl.includes('REPLACE_ME');

/**
 * Submit a plain object of { fieldKey: value } to the configured Google Form.
 * Uses no-cors, so we can't read the response — Google Forms always accepts
 * the POST, so we treat a non-throwing call as success.
 */
export async function submitToGoogleForm(data) {
  if (!isGoogleFormConfigured) {
    // Not wired up yet — log so it's obvious during testing, but don't block
    // the user-facing success screen.
    console.warn('[googleForm] Not configured — submission not sent. Add your form IDs in src/lib/googleForm.js');
    return { ok: false, skipped: true };
  }

  const body = new FormData();
  Object.entries(GOOGLE_FORM.fields).forEach(([key, entryId]) => {
    if (data[key] != null && data[key] !== '') body.append(entryId, data[key]);
  });

  await fetch(GOOGLE_FORM.actionUrl, {
    method: 'POST',
    mode: 'no-cors',
    body,
  });

  return { ok: true };
}
